import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2 } from "react-feather";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Table } from "antd";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import CustomerModal from "../../core/modals/peoples/customerModal";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader, setCustomerData, setCurrentCustomer } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, PlusCircle } from "feather-icons-react/build/IconComponents";
import { useToast } from "../../Toaster/ToastContext";
import { apiClient } from "../../services/apiClient";

const Customers = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const customerData = useSelector((state) => state.customerdata);
  const rawCurrentCustomer = useSelector(
    (state) => state.currentCustomer,
    (prev, next) => prev?.id === next?.id
  );
  const currentCustomer = useMemo(() => rawCurrentCustomer, [rawCurrentCustomer?.id]);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const isDeleting = useRef(false);
  const operationResult = useRef(null);
  const MySwal = withReactContent(Swal);
  const [pagination, setPagination] = useState({
    currentPage: 1, 
    pageSize: 10,
    totalPages: 0,
    totalCount: 0,
  });

  const fetchCustomers = useCallback(async (currentPage = 1, pageSize = 10) => {
    if (hasFetched.current || isDeleting.current) return;
    setLoading(true);
    try {
      const pageIndex = currentPage - 1; 
      const response = await apiClient.post("/paginate-customer", {
        pageIndex,
        pageSize,
        previousPageIndex: 0,
      }, false);
      if (response.success) {
        const transformedData = response.data.map((customer) => ({
          ...customer,
          customerName: customer.customer_name,
          image: customer.avatar,
        }));
        setCustomers(transformedData);
        setFilteredCustomers(transformedData);
        dispatch(setCustomerData(transformedData));
        setPagination({
          currentPage: response.currentPage || currentPage, // Use API's currentPage if provided
          pageSize: response.pageSize || pageSize,
          totalPages: response.totalPages || 1,
          totalCount: response.totalCount || 0,
        });
      } else {
        showToast(response.message || "Failed to fetch customers", "error", "Customers");
      }
    } catch (error) {
      showToast(error.message || "Failed to fetch customers", "error", "Customers");
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [showToast, dispatch]);

  useEffect(() => {
    fetchCustomers(pagination.currentPage, pagination.pageSize);
  }, [fetchCustomers, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    if (customerData && !isDeleting.current) {
      setCustomers(customerData);
      setFilteredCustomers(customerData);
    }
  }, [customerData]);

  const handleModalClose = useCallback(() => {
    if (operationResult.current?.success) {
      hasFetched.current = false; 
      fetchCustomers(pagination.currentPage, pagination.pageSize);
    }
    operationResult.current = null; 
  }, [fetchCustomers, pagination.currentPage, pagination.pageSize]);

  const handleOperationComplete = useCallback((result) => {
    operationResult.current = result; 
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    const filtered = customers.filter((item) =>
      item.customer_name.toLowerCase().includes(term) ||
      item.email.toLowerCase().includes(term) ||
      item.phone.toLowerCase().includes(term) ||
      item.country.toLowerCase().includes(term)
    );
    setFilteredCustomers(filtered);
  };

  const handleAddCustomer = () => {
    dispatch(setCurrentCustomer(null));
  };

  const handleEditCustomer = useCallback((customer) => {
    dispatch(setCurrentCustomer({ ...customer, image: customer.avatar }));
  }, [dispatch]);

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const showConfirmationAlert = useCallback(async (record) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      confirmButtonText: "Yes, delete it!",
      cancelButtonColor: "#ff0000",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          isDeleting.current = true;
          const response = await apiClient.post("/delete-customer", { id: record.id }, false);
          if (response.success) {
            showToast(response.msg || "Customer deleted successfully", "success", "Customers");
            hasFetched.current = false;
            await fetchCustomers(pagination.currentPage, pagination.pageSize);
          } else {
            showToast(response.msg || "Failed to delete customer", "error", "Customers");
          }
        } catch (error) {
          showToast(error.message || "Failed to delete customer", "error", "Customers");
        } finally {
          setLoading(false);
          isDeleting.current = false;
        }
      }
    });
  }, [fetchCustomers, showToast, MySwal, pagination.currentPage, pagination.pageSize]);

  const columns = useMemo(() => [
    {
      title: "Customer Name",
      dataIndex: "customerName",
      render: (text, record) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <ImageWithBasePath alt="Customer Image" src={record.avatar || ""} />
          </Link>
          <Link to="#">{record.customer_name}</Link>
        </span>
      ),
      sorter: (a, b) => a.customer_name.length - b.customer_name.length,
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => a.email.length - b.email.length,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => a.phone.length - b.phone.length,
    },
    {
      title: "Country",
      dataIndex: "country",
      sorter: (a, b) => a.country.length - b.country.length,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#customer-modal"
              onClick={() => handleEditCustomer(record)}
            >
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              onClick={() => showConfirmationAlert(record)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </td>
      ),
    },
  ], [handleEditCustomer, showConfirmationAlert]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Customer List</h4>
              <h6>Manage Your Customer</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => {
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            <Link
              to="#"
              className="btn btn-added"
              onClick={handleAddCustomer}
              data-bs-toggle="modal"
              data-bs-target="#customer-modal"
            >
              <PlusCircle className="me-2" />
              Add New Customer
            </Link>
          </div>
        </div>
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search by Name, Email, Phone, or Country"
                    className="form-control form-control-sm formsearch"
                    onChange={handleSearch}
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew"
                columns={columns}
                dataSource={filteredCustomers}
                rowKey={(record) => record.id}
                loading={loading}
                pagination={{
                  current: pagination.currentPage,
                  pageSize: pagination.pageSize,
                  total: pagination.totalCount,
                  onChange: async (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      currentPage: page,
                      pageSize: pageSize,
                    }));
                    hasFetched.current = false;
                    await fetchCustomers(page, pageSize);
                  },
                  showSizeChanger: true,
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <CustomerModal
        customer={currentCustomer}
        onOperationComplete={handleOperationComplete}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default Customers;